import PropTypes from "prop-types";
import { paramCase } from "change-case";
import { useState } from "react";
import { useSnackbar } from "notistack";
import { Link as RouterLink } from "react-router-dom";
import {
  Link,
  Card,
  Typography,
  CardHeader,
  Stack,
  DialogActions,
  Dialog,
  DialogContent,
  TextField,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Button,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import CategoryIcon from "@mui/icons-material/Category";
import PersonIcon from "@mui/icons-material/Person";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import Label from "../../Label";
import { isNumeric, fNumber, fCurrency } from "../../../utils/formatNumber";
import { fDate } from "../../../utils/formatTime";
import { PATH_DASHBOARD } from "../../../routes/paths";
import {
  ONLINE_STATUS,
  ONLINE_STORE_OPTIONS,
  CheckPerm,
} from "../../../utils/apis";
import useAuth from "../../../hooks/useAuth";

ChangeStatus.propTypes = {
  handleStatusUpdate: PropTypes.func,
  open: PropTypes.bool,
  loading: PropTypes.bool,
  handleStatus: PropTypes.func,
  onClose: PropTypes.func,
  status: PropTypes.string,
};
function ChangeStatus({
  open,
  loading,
  handleStatusUpdate,
  handleStatus,
  status,
  onClose,
}) {
  return (
    <Dialog
      open={open}
      maxWidth="xs"
      onClose={onClose}
      sx={{ textAlign: "center" }}
    >
      <DialogTitle>Change Status</DialogTitle>
      <DialogContent sx={{ width: { xs: "100%", md: 400 } }}>
        <DialogContentText sx={{ mb: 5 }}>
          Please set the new status below
        </DialogContentText>
        <TextField
          select
          sx={{ width: { xs: "100%", md: 500 } }}
          label="New Status"
          value={status}
          onChange={handleStatus}
        >
          {ONLINE_STATUS.map((stat) => (
            <MenuItem key={stat.value} value={stat.value}>
              {stat.label}
            </MenuItem>
          ))}
        </TextField>
      </DialogContent>
      <DialogActions>
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          spacing={2}
          sx={{ width: "100%" }}
        >
          <LoadingButton
            fullWidth
            loading={loading}
            variant="contained"
            onClick={handleStatusUpdate}
          >
            Update
          </LoadingButton>
          <Button fullWidth onClick={onClose} color="inherit">
            Cancel
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
}

OnlineAbout.propTypes = {
  profile: PropTypes.object,
  uid: PropTypes.string,
  fetch: PropTypes.func,
};
export default function OnlineAbout({ profile, uid, fetch }) {
  const hasPermission = CheckPerm("update_online_sales");
  const {
    branch,
    branchName,
    cashierName,
    cashier,
    dateAdded,
    store,
    dateUpdated,
    status,
    tax,
    due,
    total,
    products,
    date,
    orderid,
    discount,
    customer,
    customerName,
    customerPhone,
    customerEmail,
    customerAddress,
  } = profile;
  const statusText = ONLINE_STATUS[parseInt(status, 10)];
  const totalProducts = products !== "" ? JSON.parse(products).length : 0;
  const storeText = ONLINE_STORE_OPTIONS.filter(
    (opt) => opt.value === store
  )[0];
  const { updateOnlineStatus } = useAuth();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newStatus, setNewStatus] = useState(String(status));
  const { enqueueSnackbar } = useSnackbar();

  const handleStatus = (event) => {
    setNewStatus(event.target.value);
  };
  const handleStatusUpdate = async () => {
    if (newStatus) {
      try {
        setLoading(true);
        await updateOnlineStatus(newStatus, uid);
        setOpen(false);
        setNewStatus("");
        enqueueSnackbar("Status has been updated!", { variant: "success" });
        fetch();
      } catch (err) {
        enqueueSnackbar(err.message, { variant: "error" });
      }
      setLoading(false);
    } else {
      enqueueSnackbar(`Please select a new status to proceed!`, {
        variant: "error",
      });
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        border: "0.4px solid",
        borderColor: "background.border",
        boxShadow:
          "4px 2px 124px rgba(46, 53, 58, 0.02), 0px 4px 124px rgba(46, 41, 78, 0.02)",
        borderRadius: "8px",
      }}
    >
      <CardHeader title="Details" />
      <Stack spacing={2} sx={{ p: 3 }}>
        <Stack direction="row">
          <BadgeIcon
            sx={{
              width: 20,
              height: 20,
              marginTop: "2px",
              flexShrink: 0,
              marginRight: 2,
            }}
          />
          <Typography variant="body2">
            <Label color={statusText.color}>{statusText.label}</Label>
            {hasPermission && (
              <span>
                &nbsp;&nbsp;&middot;&nbsp;&nbsp;
                <Link
                  variant="subtitle2"
                  color="error.main"
                  component="button"
                  onClick={() => {
                    setOpen(true);
                  }}
                >
                  Change
                </Link>
              </span>
            )}
          </Typography>
        </Stack>
        {storeText && (
          <Stack direction="row">
            <BusinessCenterIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Store:&nbsp;&nbsp;{" "}
              <Link
                variant="subtitle2"
                color="text.primary"
                component={RouterLink}
                to={`${PATH_DASHBOARD.subpages.product}/${store}/${paramCase(
                  store
                )}`}
              >
                {storeText.label}
              </Link>
            </Typography>
          </Stack>
        )}
        <Stack direction="row">
          <CategoryIcon
            sx={{
              width: 20,
              height: 20,
              marginTop: "2px",
              flexShrink: 0,
              marginRight: 2,
            }}
          />
          <Typography variant="body2">
            Order Number:&nbsp;&nbsp; {orderid}
          </Typography>
        </Stack>
        <Stack direction="row">
          <PersonIcon
            sx={{
              width: 20,
              height: 20,
              marginTop: "2px",
              flexShrink: 0,
              marginRight: 2,
            }}
          />
          <Typography variant="body2">
            Customer:&nbsp;&nbsp;{" "}
            {customer && customerName ? (
              <Link
                variant="subtitle2"
                color="text.primary"
                component={RouterLink}
                to={`${PATH_DASHBOARD.subpages.customer}/${customer}/${paramCase(
                  customerName
                )}`}
              >
                {customerName}
              </Link>
            ) : (
              customerName || "Walk-in / None"
            )}
          </Typography>
        </Stack>
        {customerPhone && (
          <Stack direction="row">
            <PhoneIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Customer Phone:&nbsp;&nbsp; {customerPhone}
            </Typography>
          </Stack>
        )}
        {customerEmail && (
          <Stack direction="row">
            <EmailIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Customer Email:&nbsp;&nbsp; {customerEmail}
            </Typography>
          </Stack>
        )}
        {customerAddress && (
          <Stack direction="row">
            <HomeIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Customer Address:&nbsp;&nbsp; {customerAddress}
            </Typography>
          </Stack>
        )}
        <Stack direction="row">
          <CategoryIcon
            sx={{
              width: 20,
              height: 20,
              marginTop: "2px",
              flexShrink: 0,
              marginRight: 2,
            }}
          />
          <Typography variant="body2">
            Total products:&nbsp;&nbsp;{" "}
            {isNumeric(totalProducts) ? fNumber(totalProducts) : ""}
          </Typography>
        </Stack>
        <Stack direction="row">
          <CategoryIcon
            sx={{
              width: 20,
              height: 20,
              marginTop: "2px",
              flexShrink: 0,
              marginRight: 2,
            }}
          />
          <Typography variant="body2">
            Total Amount:&nbsp;&nbsp; {isNumeric(total) ? fCurrency(total) : ""}
          </Typography>
        </Stack>
        <Stack direction="row">
          <CategoryIcon
            sx={{
              width: 20,
              height: 20,
              marginTop: "2px",
              flexShrink: 0,
              marginRight: 2,
            }}
          />
          <Typography variant="body2">
            Discount:&nbsp;&nbsp;{" "}
            {isNumeric(discount) ? fCurrency(discount) : ""}
          </Typography>
        </Stack>
        {tax > 0 && (
          <Stack direction="row">
            <CategoryIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Balance:&nbsp;&nbsp; {isNumeric(tax) ? fCurrency(tax) : ""}
            </Typography>
          </Stack>
        )}
        {due !== "" && (
          <Stack direction="row">
            <CalendarTodayIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Due On: {due !== "0000-00-00" ? fDate(due) : "Not Specified"}
            </Typography>
          </Stack>
        )}
        {date !== "" && (
          <Stack direction="row">
            <CalendarTodayIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Date On: {date !== "0000-00-00" ? fDate(date) : "Not Specified"}
            </Typography>
          </Stack>
        )}
        {cashierName !== "" && (
          <Stack direction="row">
            <BusinessCenterIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Added By:&nbsp;&nbsp;{" "}
              <Link
                variant="subtitle2"
                color="text.primary"
                component={RouterLink}
                to={`${PATH_DASHBOARD.subpages.cashier}/${cashier}/${paramCase(
                  cashierName
                )}`}
              >
                {cashierName}
              </Link>
            </Typography>
          </Stack>
        )}
        {branchName !== "" && (
          <Stack direction="row">
            <BusinessCenterIcon
              sx={{
                width: 20,
                height: 20,
                marginTop: "2px",
                flexShrink: 0,
                marginRight: 2,
              }}
            />
            <Typography variant="body2">
              Branch:&nbsp;&nbsp;{" "}
              {branch ? (
                <Link
                  variant="subtitle2"
                  color="text.primary"
                  component={RouterLink}
                  to={`${PATH_DASHBOARD.subpages.branch}/${branch}/${paramCase(
                    branchName
                  )}`}
                >
                  {branchName}
                </Link>
              ) : (
                branchName
              )}
            </Typography>
          </Stack>
        )}
        <Stack direction="row">
          <CalendarTodayIcon
            sx={{
              width: 20,
              height: 20,
              marginTop: "2px",
              flexShrink: 0,
              marginRight: 2,
            }}
          />
          <Typography variant="body2">
            Added on{" "}
            {dateAdded !== "0000-00-00" ? fDate(dateAdded) : "Not Specified"}
          </Typography>
        </Stack>
        <Stack direction="row">
          <CalendarTodayIcon
            sx={{
              width: 20,
              height: 20,
              marginTop: "2px",
              flexShrink: 0,
              marginRight: 2,
            }}
          />
          <Typography variant="body2">
            Last updated on{" "}
            {dateUpdated !== "0000-00-00"
              ? fDate(dateUpdated)
              : "Not Specified"}
          </Typography>
        </Stack>
      </Stack>
      <ChangeStatus
        open={open}
        loading={loading}
        handleStatus={handleStatus}
        status={newStatus}
        handleStatusUpdate={handleStatusUpdate}
        onClose={() => {
          setOpen(false);
        }}
      />
    </Card>
  );
}
